import { View, StyleSheet, Pressable } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import Animated, { FadeIn } from "react-native-reanimated";
import { TierBody } from "@dramadetector/ui";
import { useTranslation } from "@dramadetector/shared";

interface SharedLinkErrorProps {
  error: string;
  onNewAnalysis: () => void;
}

/**
 * Error content provider for shared link errors
 */
function getErrorContent(error: string, t: any) {
  switch (error) {
    case "invalid_format":
      return {
        title: t("home.errors.shared_link_invalid_token_title", {
          defaultValue: "Invalid Link",
        }),
        message: t("home.errors.shared_link_invalid_token_message", {
          defaultValue: "This link appears to be corrupted or incomplete. Please check the URL and try again.",
        }),
      };
    case "not_found":
      return {
        title: t("home.errors.shared_link_not_found_title", {
          defaultValue: "Link Not Found",
        }),
        message: t("home.errors.shared_link_not_found_message", {
          defaultValue: "This link doesn't exist or has been deleted.",
        }),
      };
    case "expired":
      return {
        title: t("home.errors.shared_link_expired_title", {
          defaultValue: "Link Expired",
        }),
        message: t("home.errors.shared_link_expired_message", {
          defaultValue: "This shared link has expired. Ask the sender to create a new link.",
        }),
      };
    case "revoked":
      return {
        title: t("home.errors.shared_link_revoked_title", {
          defaultValue: "Link Revoked",
        }),
        message: t("home.errors.shared_link_revoked_message", {
          defaultValue: "The sender has revoked this link. Ask them to create a new one if you still need access.",
        }),
      };
    default:
      return {
        title: t("home.errors.shared_link_invalid", {
          defaultValue: "Link Not Found",
        }),
        message: t("home.errors.shared_link_invalid_message", {
          defaultValue: "This shared link is invalid, expired, or has been deleted.",
        }),
      };
  }
}

/**
 * Error state component for shared links
 */
export function SharedLinkError({ error, onNewAnalysis }: SharedLinkErrorProps) {
  const { t } = useTranslation("home");
  const errorContent = getErrorContent(error, t);

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={["#0a0012", "#1a0a2e", "#0d1117"]}
        style={StyleSheet.absoluteFill}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      />
      <View style={styles.errorContainer}>
        <TierBody size="lg" style={styles.errorTitle}>
          {errorContent.title}
        </TierBody>
        <TierBody size="base" style={styles.errorMessage}>
          {errorContent.message}
        </TierBody>
        <Animated.View entering={FadeIn.delay(300)} style={styles.errorAction}>
          <Pressable onPress={onNewAnalysis}>
            <TierBody size="base" style={styles.backLink}>
              {t("home.modern.try_drama_detector", {
                defaultValue: "→ Try DramaDetector",
              })}
            </TierBody>
          </Pressable>
        </Animated.View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    gap: 16,
  },
  errorTitle: {
    marginBottom: 8,
    color: '#EF4444',
  },
  errorMessage: {
    textAlign: "center",
    maxWidth: 400,
    color: '#9CA3AF',
  },
  errorAction: {
    marginTop: 24,
  },
  backLink: {
    textDecorationLine: "underline",
    color: '#A855F7',
  },
});
